import React from 'react';
import { Button, Spin } from 'antd';
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled,
  CloseCircleFilled,
  ClockCircleFilled,
  LoadingOutlined,
  EyeOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { EXECUTE_STATUS, INTENT_TYPE } from './contracts';
import './aiCrew.css';

/**
 * ResultView - Post-execution status screen for CrewCall Agent.
 *
 * Rules:
 * - One distinct view per execute status (SUCCESS, PARTIAL, CONFLICT, FAILED, EXPIRED).
 * - Never "success" when anything failed.
 * - Never blame the user.
 * - Never show a stack trace, error code, or traceId to the supervisor (traceId is logged to console).
 */
export default function ResultView({
  result,
  proposal,
  selectedCount = 0,
  onViewCrew,
  onCreateAnother,
  onRetryExecute,
  onBackToProposal,
  isRetrying = false,
  onReconnectUkg,
}) {
  if (!result) return null;

  const status = Object.values(EXECUTE_STATUS).includes(result.status) ? result.status : EXECUTE_STATUS.FAILED;
  // transfer/copy proposals carry the crew's name at the top level, not under `crew`
  const crewName = result.crewName || proposal?.crew?.crewName || proposal?.crewName || 'Crew';
  const scheduledCount = result.scheduled ?? result.scheduledCount ?? 0;
  const requestedCount = result.requested ?? selectedCount;
  const intentType = proposal?.intentType;
  const isTransfer = intentType === INTENT_TYPE.TRANSFER_OWNER;
  const isCopy = intentType === INTENT_TYPE.COPY_CREW;
  const isAdd = intentType === INTENT_TYPE.ADD_TO_CREW;
  const isRemove = intentType === INTENT_TYPE.REMOVE_FROM_CREW;
  const isLead = intentType === INTENT_TYPE.SET_CREW_LEAD;
  const isMemberChange = isAdd || isRemove || isLead;
  const person = proposal?.employeeName || proposal?.employeeId;

  if (result.recoveryRequired) {
    return <div className="ai-crew-result-container" data-testid="ai-crew-result-recovery" role="alert">
      <ExclamationCircleFilled style={{ fontSize: 64, color: '#ea580c' }} />
      <h2 className="ai-crew-result-title">Scheduling Needs Verification</h2>
      <p className="ai-crew-result-subtitle">{result.message || 'UKG could not confirm the final outcome. Some changes may already have been saved.'}</p>
      <p>Check this crew in VYN and UKG before scheduling again to avoid duplicate shifts.</p>
      {result.crewId && <Button type="primary" size="large" onClick={onViewCrew}>View in Crew Dashboard</Button>}
    </div>;
  }

  // 1. SUCCESS SCREEN
  if (status === EXECUTE_STATUS.SUCCESS) {
    return (
      <div className="ai-crew-result-container" data-testid="ai-crew-result-success">
        <div className="ai-crew-result-icon">
          <CheckCircleFilled style={{ fontSize: 64, color: '#10b981' }} />
        </div>
        <h2 className="ai-crew-result-title">
          {isTransfer
            ? `${crewName} Transferred`
            : isCopy
              ? `${crewName} Copied`
              : isAdd ? `${person} Added to ${crewName}`
                : isRemove ? `${person} Removed from ${crewName}`
                  : isLead ? `${crewName} Has a New Lead`
                    : `${crewName} Successfully Scheduled`}
        </h2>
        <p className="ai-crew-result-subtitle">
          {isTransfer && (
            <>Ownership moved to <strong>{proposal?.newOwnerName || proposal?.newOwnerId}</strong>.</>
          )}
          {isCopy && (
            <>Copied to <strong>{proposal?.targetDateLabel || proposal?.targetDate}</strong>.</>
          )}
          {isAdd && <>Scheduled in VYN and confirmed in UKG.</>}
          {isRemove && <>Taken off the crew in VYN and the UKG shift is deleted.</>}
          {isLead && <>Crew lead is now <strong>{proposal?.newLeadLabel || person}</strong>.</>}
          {!isTransfer && !isCopy && !isMemberChange && (
            <>All <strong>{scheduledCount}</strong> workers confirmed and scheduled without conflict.</>
          )}
        </p>

        {proposal?.crew?.eventName && (
          <div style={{ fontSize: 13, color: '#64748b', background: '#f8fafc', padding: '6px 16px', borderRadius: 20, border: '1px solid #e2e8f0' }}>
            {proposal.crew.eventName} · {proposal.crew.taskName || 'Move-in'}
          </div>
        )}

        <div className="ai-crew-result-actions">
          <Button
            type="primary"
            size="large"
            icon={<EyeOutlined />}
            onClick={onViewCrew}
            className="ai-crew-result-btn"
          >
            View in Crew Dashboard
          </Button>
          <Button
            size="large"
            icon={<PlusOutlined />}
            onClick={onCreateAnother}
            className="ai-crew-result-btn"
          >
            Schedule Another Crew
          </Button>
        </div>
      </div>
    );
  }

  // 2. PARTIAL SCREEN (Volcano tone per spec)
  if (status === EXECUTE_STATUS.PARTIAL) {
    const failedList = result.failed || [];
    return (
      <div className="ai-crew-result-container" data-testid="ai-crew-result-partial">
        <div className="ai-crew-result-icon">
          <ExclamationCircleFilled style={{ fontSize: 64, color: '#ea580c' }} />
        </div>
        <h2 className="ai-crew-result-title">
          {isMemberChange ? 'Crew Change Partially Completed' : isCopy ? 'Crew Copy Partially Completed' : 'Crew Created with Partial Schedule'}
        </h2>
        <p className="ai-crew-result-subtitle">
          {isMemberChange ? result.message || 'Only part of the requested change was completed. Check this crew before making another change.'
            : <>Crew was established with <strong>{scheduledCount}</strong> of <strong>{requestedCount}</strong> workers confirmed.</>}
        </p>

        {failedList.length > 0 && (
          <div className="ai-crew-partial-failed-box">
            <div style={{ fontWeight: 700, color: '#c2410c', fontSize: 14, marginBottom: 8 }}>
              Scheduling constraints on {failedList.length} worker{failedList.length > 1 ? 's' : ''}:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {failedList.map((worker, idx) => (
                <div key={worker.employeeId || idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, borderBottom: '1px dashed #fed7aa', padding: '4px 0' }}>
                  <span style={{ fontWeight: 600, color: '#0f172a' }}>{worker.name}</span>
                  <span style={{ color: '#64748b' }}>
                    {worker.reason && typeof worker.reason === 'object'
                      ? (worker.reason.message || worker.reason.error || JSON.stringify(worker.reason))
                      : (worker.reason && worker.reason !== '[object Object]' ? worker.reason : 'Scheduling constraint in UKG')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ fontSize: 13, color: '#64748b', maxWidth: 480, marginTop: 8, lineHeight: 1.5 }}>
          The crew record is saved in VYN. You can backfill or adjust the remaining slots directly from the crew management screen.
        </div>

        <div className="ai-crew-result-actions">
          <Button
            type="primary"
            size="large"
            icon={<EyeOutlined />}
            onClick={onViewCrew}
            className="ai-crew-result-btn"
          >
            View in Crew Dashboard
          </Button>
          <Button
            size="large"
            icon={<PlusOutlined />}
            onClick={onCreateAnother}
            className="ai-crew-result-btn"
          >
            Schedule Another Crew
          </Button>
        </div>
      </div>
    );
  }

  // 3. CONFLICT SCREEN (Blue tone per spec: nothing broke!)
  if (status === EXECUTE_STATUS.CONFLICT) {
    const conflicts = result.conflicts || [];
    const replacements = result.replacements || [];
    return (
      <div className="ai-crew-result-container" data-testid="ai-crew-result-conflict">
        <div className="ai-crew-result-icon">
          <InfoCircleFilled style={{ fontSize: 64, color: '#1677ff' }} />
        </div>
        <h2 className="ai-crew-result-title">
          Schedule Updated During Review
        </h2>
        <p className="ai-crew-result-subtitle">
          {conflicts.length} worker{conflicts.length > 1 ? 's were' : ' was'} assigned elsewhere while this proposal was open. Replacements have been automatically proposed.
        </p>

        <div className="ai-crew-conflict-box">
          {conflicts.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, color: '#dc2626', fontSize: 13, marginBottom: 6 }}>
                Booked on other shifts:
              </div>
              {conflicts.map((c, idx) => (
                <div key={c.employeeId || idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}>
                  <span style={{ fontWeight: 600, color: '#0f172a' }}>{c.name}</span>
                  <span style={{ color: '#64748b' }}>{c.detail || 'Conflicting schedule'}</span>
                </div>
              ))}
            </div>
          )}

          {replacements.length > 0 && (
            <div>
              <div style={{ fontWeight: 700, color: '#16a34a', fontSize: 13, marginBottom: 6 }}>
                Eligible replacements ready to swap in:
              </div>
              {replacements.map((r, idx) => (
                <div key={r.employeeId || idx} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '3px 0' }}>
                  <CheckOutlined style={{ color: '#16a34a', fontWeight: 'bold' }} />
                  <span style={{ fontWeight: 600, color: '#0f172a' }}>{r.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="ai-crew-result-actions">
          <Button
            type="primary"
            size="large"
            loading={isRetrying}
            onClick={onRetryExecute}
            className="ai-crew-result-btn"
          >
            Confirm & Schedule Updated Crew
          </Button>
          <Button
            size="large"
            icon={<ArrowLeftOutlined />}
            onClick={onBackToProposal}
            className="ai-crew-result-btn"
          >
            Review Proposal
          </Button>
        </div>
      </div>
    );
  }

  // 4. FAILED SCREEN
  if (status === EXECUTE_STATUS.FAILED) {
    const cleanMessage =
      result.message ||
      'Crew creation could not be completed at this time. Your worker selection has been preserved.';

    return (
      <div className="ai-crew-result-container" data-testid="ai-crew-result-failed">
        <div className="ai-crew-result-icon">
          <CloseCircleFilled style={{ fontSize: 64, color: '#ef4444' }} />
        </div>
        <h2 className="ai-crew-result-title">
          Could Not Complete Scheduling
        </h2>
        <p className="ai-crew-result-subtitle" style={{ color: '#dc2626' }}>
          {cleanMessage}
        </p>

        <div className="ai-crew-result-actions">
          {result.code === 'UKG_REAUTH_REQUIRED' && (
            <Button type="primary" size="large" onClick={onReconnectUkg}>
              Reconnect UKG
            </Button>
          )}
          <Button
            type={result.code === 'UKG_REAUTH_REQUIRED' ? 'default' : 'primary'}
            size="large"
            icon={<ArrowLeftOutlined />}
            onClick={onBackToProposal}
            className="ai-crew-result-btn"
          >
            Back to Proposal
          </Button>
        </div>
      </div>
    );
  }

  // 5. EXPIRED SCREEN
  if (status === EXECUTE_STATUS.EXPIRED) {
    return (
      <div className="ai-crew-result-container" data-testid="ai-crew-result-expired">
        <div className="ai-crew-result-icon">
          <ClockCircleFilled style={{ fontSize: 64, color: '#94a3b8' }} />
        </div>
        <h2 className="ai-crew-result-title">
          Proposal Window Expired
        </h2>
        <p className="ai-crew-result-subtitle">
          This proposal is over 30 minutes old. CrewCall Agent is refreshing availability to guarantee accurate scheduling...
        </p>
        <div style={{ marginTop: 24 }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: '#1677ff' }} spin />} />
        </div>
      </div>
    );
  }

  return null;
}
